import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaEye, FaPen, FaFilePdf, FaClipboardList, FaTimes, FaInfoCircle } from "react-icons/fa";
import * as api from "../api/placementApi";
import {
  T, OUTCOME_OPTIONS, INTERVIEW_PARAMETERS, GlobalStyles, Spinner, PillSelect, StagePill, StarRating,
  overlayStyle, modalStyle, primaryBtnStyle, textBtnStyle, inputStyle, labelStyle,
  TEST_SECTIONS, emptySectionMarks, sectionTheadStyle, sectionTdStyle, sectionInputStyle,
} from "./uiKit";

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
}

const RESULT_WINDOW_HOURS = 3;

// View / Resume / Edit-Result access is all time-boxed to the scheduled
// slot: locked before it starts (nothing to view/log yet), open from the
// scheduled time through RESULT_WINDOW_HOURS after it, then locked again —
// viewing or logging a result long after the fact goes through the admin
// Recruitment page instead. Entries with no scheduledDate at all (legacy
// data) fail open rather than being permanently stuck.
function getResultWindowStatus(scheduledDate) {
  if (!scheduledDate) return { enabled: true, reason: "" };
  const start = new Date(scheduledDate).getTime();
  const end = start + RESULT_WINDOW_HOURS * 60 * 60 * 1000;
  const now = Date.now();
  if (now < start) {
    return { enabled: false, reason: `Opens at ${formatDateTime(scheduledDate)}` };
  }
  if (now > end) {
    return { enabled: false, reason: `Window closed ${RESULT_WINDOW_HOURS}h after the scheduled time` };
  }
  return { enabled: true, reason: "" };
}

const OUTCOME_STYLE = {
  PASSED: { bg: T.greenSoft, fg: T.green, label: "Passed" },
  FAILED: { bg: T.dangerSoft, fg: T.danger, label: "Failed" },
  ON_HOLD: { bg: T.amberSoft, fg: "#b45309", label: "On Hold" },
};

// Continuously scrolling info banner explaining the RESULT_WINDOW_HOURS
// gating above — the disabled-button tooltips only surface that rule to
// someone who's already tried to click, so this puts it in front of
// everyone up top instead. Classic marquee trick: the track renders the
// message twice back to back and translates by exactly -50%, so the
// moment the first copy scrolls fully offscreen the second is in the exact
// same position it started in — the loop reset is invisible.
function InfoTicker({ text, style }) {
  return (
    <div style={{ overflow: "hidden", ...style }}>
      <style>{`
        @keyframes plc-ticker-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .plc-ticker-track { display: inline-flex; white-space: nowrap; animation: plc-ticker-scroll 22s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .plc-ticker-track { animation: none; } }
      `}</style>
      <div className="plc-ticker-track">
        {[0, 1].map((i) => (
          <span key={i} style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "9px 40px", fontSize: 12.5, fontWeight: 600, color: T.danger,
          }}>
            <FaInfoCircle size={13} style={{ flexShrink: 0 }} />
            {text}
          </span>
        ))}
      </div>
    </div>
  );
}

function OutcomePill({ outcome }) {
  const s = OUTCOME_STYLE[outcome];
  if (!s) return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: "#f1f5f9", color: T.ink3 }}>Pending</span>;
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11.5,
      fontWeight: 700, letterSpacing: ".2px", background: s.bg, color: s.fg, fontFamily: T.fontBody,
    }}>
      {s.label}
    </span>
  );
}

const theadCellStyle = {
  textAlign: "left", padding: "11px 16px", fontSize: 11.5, fontWeight: 700, color: T.ink3,
  textTransform: "uppercase", letterSpacing: ".4px", fontFamily: T.fontDisplay,
  position: "sticky", top: 0, background: "#fafbfc", zIndex: 1,
};
const tdCellStyle = {
  padding: "13px 16px", fontSize: 13.5, color: T.ink2, verticalAlign: "middle",
};
const iconBtnStyle = {
  border: "none", background: "transparent", color: T.ink2, cursor: "pointer",
  fontSize: 13, padding: 7, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center",
};

/* ── Resume viewer modal ── same "fetch as Blob, embed via object URL"
   approach as the admin Recruitment page's ResumeViewerModal (auth token
   can't be attached to a plain <iframe src>). Kept as a local copy rather
   than importing from PlacementAdminPage.js, which doesn't export it. ── */
function ResumeViewerModal({ candidateId, fileName, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    let objUrl = null;
    setLoading(true);
    setError(false);
    api.fetchResumeBlob(candidateId)
      .then((blob) => {
        if (!active) return;
        objUrl = URL.createObjectURL(blob);
        setBlobUrl(objUrl);
      })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => {
      active = false;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [candidateId]);

  return (
    <div style={{ ...overlayStyle, zIndex: 1200 }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: "min(920px, 94vw)", height: "92vh", background: T.white, borderRadius: T.radius,
        boxShadow: T.shadowMd, display: "flex", flexDirection: "column", overflow: "hidden",
        fontFamily: T.fontBody, animation: "plc-fade-in .16s ease",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
            <FaFilePdf color={T.danger} size={15} />
            <span style={{ fontSize: 14, fontWeight: 600, color: T.ink1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {fileName || "Resume"}
            </span>
          </div>
          <button type="button" onClick={onClose} title="Close" style={iconBtnStyle}>
            <FaTimes size={15} />
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, background: "#525659" }}>
          {loading ? (
            <Spinner padding="160px 0" />
          ) : error ? (
            <div style={{ color: "#fff", textAlign: "center", padding: 60, fontSize: 13.5 }}>Failed to load resume.</div>
          ) : (
            <iframe src={blobUrl} title="Resume Preview" style={{ width: "100%", height: "100%", border: "none" }} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Test Result preview modal ── shown from the Interview tab's "Test
   Result" column, so an interviewer can check how the candidate did at
   Test before conducting the interview, without switching to the Test tab
   or asking the admin. Read-only, same section-marks table as elsewhere. ── */
function TestResultPreviewModal({ testResult, candidateName, onClose }) {
  const hasSectionMarks = testResult.sectionMarks && TEST_SECTIONS.some((s) => testResult.sectionMarks[s.key]);

  const detailsBlock = (
    <>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Name</label>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: T.ink1 }}>{candidateName}</div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Score / Rating</label>
        <div style={{ fontSize: 13.5, color: T.ink1 }}>{testResult.score || "—"}</div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Outcome</label>
        <div><OutcomePill outcome={testResult.outcome} /></div>
      </div>

      <div style={{ marginBottom: 6 }}>
        <label style={labelStyle}>Feedback</label>
        <div style={{ fontSize: 13.5, color: T.ink1, whiteSpace: "pre-wrap" }}>{testResult.feedback || "—"}</div>
      </div>
    </>
  );

  const sectionMarksTable = (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>Section-wise Marks</label>
      <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={sectionTheadStyle}>Section</th>
              <th style={{ ...sectionTheadStyle, textAlign: "right" }}>Obtain Marks</th>
              <th style={{ ...sectionTheadStyle, textAlign: "right" }}>Section Marks</th>
            </tr>
          </thead>
          <tbody>
            {TEST_SECTIONS.map((s) => {
              const row = testResult.sectionMarks[s.key] || {};
              return (
                <tr key={s.key} style={{ borderTop: `1px solid ${T.border}` }}>
                  <td style={sectionTdStyle}>{s.label}</td>
                  <td style={{ ...sectionTdStyle, textAlign: "right", fontFamily: T.fontMono }}>{row.obtained || "0"}</td>
                  <td style={{ ...sectionTdStyle, textAlign: "right", fontFamily: T.fontMono }}>{row.total || "0"}</td>
                </tr>
              );
            })}
            <tr style={{ borderTop: `1px solid ${T.border}`, background: "#f8fafc" }}>
              <td style={{ ...sectionTdStyle, fontWeight: 700, color: T.ink1 }}>Total Score</td>
              <td style={{ ...sectionTdStyle, textAlign: "right", fontWeight: 700, color: T.ink1, fontFamily: T.fontMono }}>
                {TEST_SECTIONS.reduce((sum, s) => sum + (Number(testResult.sectionMarks[s.key]?.obtained) || 0), 0)}
              </td>
              <td style={{ ...sectionTdStyle, textAlign: "right", fontWeight: 700, color: T.ink1, fontFamily: T.fontMono }}>
                {TEST_SECTIONS.reduce((sum, s) => sum + (Number(testResult.sectionMarks[s.key]?.total) || 0), 0)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{`
        @media (max-width: 700px) {
          .plc-testresult-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div style={{ ...modalStyle, maxWidth: hasSectionMarks ? 700 : 440, width: "94%", maxHeight: "88vh", padding: 0, display: "flex", flexDirection: "column" }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "20px 24px", borderBottom: `1px solid ${T.border}`, flexShrink: 0,
        }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>Test Result</span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        <div className="plc-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 24px" }}>
          {hasSectionMarks ? (
            <div className="plc-testresult-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              <div style={{ minWidth: 0 }}>{sectionMarksTable}</div>
              <div style={{ minWidth: 0 }}>{detailsBlock}</div>
            </div>
          ) : (
            detailsBlock
          )}
        </div>

        <div style={{
          display: "flex", justifyContent: "flex-end",
          padding: "14px 24px", borderTop: `1px solid ${T.border}`, flexShrink: 0,
        }}>
          <button type="button" onClick={onClose} style={textBtnStyle}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ── View Result modal ── read-only: name + result details (score,
   outcome, feedback) only — remarks are deliberately left out here, since
   this view is meant to show the CANDIDATE's result, not internal/logistics
   notes. Editing (which does include remarks) is a separate action. ── */
function ViewResultModal({ entry, onClose }) {
  const stageLabel = entry.stage === "TEST" ? "Test" : "Interview";
  const isInterview = entry.stage === "INTERVIEW";
  const hasSectionMarks = entry.stage === "TEST" && entry.sectionMarks && TEST_SECTIONS.some((s) => entry.sectionMarks[s.key]);

  const scoreOutcomeReadBlock = (
    <>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Score / Rating</label>
        <div style={{ fontSize: 13.5, color: T.ink1 }}>{entry.score || "—"}</div>
      </div>

      <div style={{ marginBottom: 0 }}>
        <label style={labelStyle}>Outcome</label>
        <div><OutcomePill outcome={entry.outcome} /></div>
      </div>
    </>
  );

  const feedbackReadBlock = (
    <>
      <div style={{ marginBottom: isInterview ? 14 : 6 }}>
        <label style={labelStyle}>Feedback</label>
        <div style={{ fontSize: 13.5, color: T.ink1, whiteSpace: "pre-wrap" }}>{entry.feedback || "—"}</div>
      </div>

      {isInterview && (
        <div style={{ marginBottom: 6 }}>
          <label style={labelStyle}>Scorecard</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {INTERVIEW_PARAMETERS.map((p) => (
              <div key={p.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, color: T.ink2 }}>{p.label}</span>
                <StarRating value={entry.evaluation?.[p.key]} readOnly size={14} />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{`
        @media (max-width: 700px) {
          .plc-viewresult-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div style={{ ...modalStyle, maxWidth: hasSectionMarks ? 700 : 560, width: "94%", maxHeight: "88vh", padding: 0, display: "flex", flexDirection: "column" }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "20px 24px", borderBottom: `1px solid ${T.border}`, flexShrink: 0,
        }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>{stageLabel} Result</span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        <div className="plc-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 24px" }}>
          <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 18 }}>
            {entry.positionApplied || "Role not specified"}{entry.roundLabel ? ` · ${entry.roundLabel}` : ""}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Name</label>
            <div style={{ fontSize: 14.5, fontWeight: 600, color: T.ink1 }}>{entry.candidateName}</div>
          </div>

          <div className="plc-viewresult-grid" style={{ display: "grid", gridTemplateColumns: hasSectionMarks ? "1fr 1fr" : "1fr 1fr", gap: 24 }}>
            {hasSectionMarks ? (
              <>
                <div style={{ minWidth: 0 }}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Section-wise Marks</label>
                    <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th style={sectionTheadStyle}>Section</th>
                            <th style={{ ...sectionTheadStyle, textAlign: "right" }}>Obtain Marks</th>
                            <th style={{ ...sectionTheadStyle, textAlign: "right" }}>Section Marks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {TEST_SECTIONS.map((s) => {
                            const row = entry.sectionMarks[s.key] || {};
                            return (
                              <tr key={s.key} style={{ borderTop: `1px solid ${T.border}` }}>
                                <td style={sectionTdStyle}>{s.label}</td>
                                <td style={{ ...sectionTdStyle, textAlign: "right", fontFamily: T.fontMono }}>{row.obtained || "0"}</td>
                                <td style={{ ...sectionTdStyle, textAlign: "right", fontFamily: T.fontMono }}>{row.total || "0"}</td>
                              </tr>
                            );
                          })}
                          <tr style={{ borderTop: `1px solid ${T.border}`, background: "#f8fafc" }}>
                            <td style={{ ...sectionTdStyle, fontWeight: 700, color: T.ink1 }}>Total Score</td>
                            <td style={{ ...sectionTdStyle, textAlign: "right", fontWeight: 700, color: T.ink1, fontFamily: T.fontMono }}>
                              {TEST_SECTIONS.reduce((sum, s) => sum + (Number(entry.sectionMarks[s.key]?.obtained) || 0), 0)}
                            </td>
                            <td style={{ ...sectionTdStyle, textAlign: "right", fontWeight: 700, color: T.ink1, fontFamily: T.fontMono }}>
                              {TEST_SECTIONS.reduce((sum, s) => sum + (Number(entry.sectionMarks[s.key]?.total) || 0), 0)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div style={{ minWidth: 0 }}>{scoreOutcomeReadBlock}{feedbackReadBlock}</div>
              </>
            ) : (
              <>
                <div style={{ minWidth: 0 }}>{scoreOutcomeReadBlock}</div>
                <div style={{ minWidth: 0 }}>{feedbackReadBlock}</div>
              </>
            )}
          </div>
        </div>

        <div style={{
          display: "flex", justifyContent: "flex-end",
          padding: "14px 24px", borderTop: `1px solid ${T.border}`, flexShrink: 0,
        }}>
          <button type="button" onClick={onClose} style={textBtnStyle}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ── Add/Edit Result modal ── score / outcome / feedback only — deliberately
   NO remarks field here (unlike the admin Recruitment page's LogResultModal):
   remarks are internal/logistics notes, out of scope for this self-service
   page. Not sending "remarks" in the update payload at all (rather than
   sending it as null) means any remarks already set elsewhere are left
   untouched. ── */
function AddResultModal({ entry, onClose, onDone }) {
  const [score, setScore] = useState(entry.score || "");
  const [outcome, setOutcome] = useState(entry.outcome || "");
  const [feedback, setFeedback] = useState(entry.feedback || "");
  const [ratings, setRatings] = useState(() => {
    const r = {};
    INTERVIEW_PARAMETERS.forEach((p) => { r[p.key] = entry.evaluation?.[p.key] ?? null; });
    return r;
  });
  const [sectionMarks, setSectionMarks] = useState(() => {
    const saved = entry.sectionMarks;
    if (!saved || typeof saved !== "object") return emptySectionMarks();
    const base = emptySectionMarks();
    TEST_SECTIONS.forEach((s) => {
      if (saved[s.key]) base[s.key] = { obtained: String(saved[s.key].obtained ?? ""), total: String(saved[s.key].total ?? "") };
    });
    return base;
  });
  const [saving, setSaving] = useState(false);

  const isInterview = entry.stage === "INTERVIEW";
  const isTest = entry.stage === "TEST";
  const setRating = (key) => (v) => setRatings((r) => ({ ...r, [key]: v }));
  const setSectionMark = (key, field) => (e) => {
    const raw = e.target.value.replace(/[^\d]/g, "");
    setSectionMarks((m) => {
      const next = { ...m[key], [field]: raw };
      // Obtained marks can never exceed that section's max — whether from
      // typing more than allowed, or shrinking the max below what was
      // already entered, clamp Obtained down to match.
      if (next.obtained !== "" && next.total !== "" && Number(next.obtained) > Number(next.total)) {
        next.obtained = next.total;
      }
      return { ...m, [key]: next };
    });
  };

  const totalObtained = TEST_SECTIONS.reduce((sum, s) => sum + (Number(sectionMarks[s.key].obtained) || 0), 0);
  const totalMax = TEST_SECTIONS.reduce((sum, s) => sum + (Number(sectionMarks[s.key].total) || 0), 0);
  const anySectionMarkEntered = TEST_SECTIONS.some((s) => sectionMarks[s.key].obtained !== "" || sectionMarks[s.key].total !== "");

  // Keeps Score/Rating in sync with the section breakdown as it's filled
  // in — still a plain editable field, so typing over it directly (without
  // touching the table) works exactly as before.
  useEffect(() => {
    if (!isTest || !anySectionMarkEntered) return;
    setScore(totalMax ? `${totalObtained}/${totalMax}` : String(totalObtained));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalObtained, totalMax, anySectionMarkEntered, isTest]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateStageEntry(entry.candidateId, entry.historyId, {
        score: score || null, outcome: outcome || null, feedback: feedback || null,
        ...(isInterview ? ratings : {}),
        ...(isTest && anySectionMarkEntered ? { sectionMarks } : {}),
      });
      toast.success("Result saved");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to save result");
    } finally {
      setSaving(false);
    }
  };

  const stageLabel = entry.stage === "TEST" ? "Test" : "Interview";

  const sectionMarksTable = (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>Section-wise Marks</label>
      <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={sectionTheadStyle}>Section</th>
              <th style={{ ...sectionTheadStyle, textAlign: "right" }}>Obtain Marks</th>
              <th style={{ ...sectionTheadStyle, textAlign: "right" }}>Section Marks</th>
            </tr>
          </thead>
          <tbody>
            {TEST_SECTIONS.map((s) => (
              <tr key={s.key} style={{ borderTop: `1px solid ${T.border}` }}>
                <td style={sectionTdStyle}>{s.label}</td>
                <td style={{ ...sectionTdStyle, textAlign: "right" }}>
                  <input
                    inputMode="numeric"
                    style={sectionInputStyle}
                    value={sectionMarks[s.key].obtained}
                    onChange={setSectionMark(s.key, "obtained")}
                    placeholder="0"
                  />
                </td>
                <td style={{ ...sectionTdStyle, textAlign: "right" }}>
                  <input
                    inputMode="numeric"
                    style={sectionInputStyle}
                    value={sectionMarks[s.key].total}
                    onChange={setSectionMark(s.key, "total")}
                    placeholder="0"
                  />
                </td>
              </tr>
            ))}
            <tr style={{ borderTop: `1px solid ${T.border}`, background: "#f8fafc" }}>
              <td style={{ ...sectionTdStyle, fontWeight: 700, color: T.ink1 }}>Total Score</td>
              <td style={{ ...sectionTdStyle, textAlign: "right", fontWeight: 700, color: T.ink1, fontFamily: T.fontMono }}>{totalObtained}</td>
              <td style={{ ...sectionTdStyle, textAlign: "right", fontWeight: 700, color: T.ink1, fontFamily: T.fontMono }}>{totalMax}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const scoreOutcomeBlock = (
    <>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Score / Rating</label>
        <input style={inputStyle} value={score} onChange={(e) => setScore(e.target.value)} placeholder="e.g. 78/100 or 4/5" />
      </div>

      <div style={{ marginBottom: 0 }}>
        <label style={labelStyle}>Outcome</label>
        <PillSelect value={outcome} options={[{ value: "", label: "Not decided yet" }, ...OUTCOME_OPTIONS]} onChange={setOutcome} placeholder="Not decided yet" />
      </div>
    </>
  );

  const feedbackBlock = (
    <>
      <div style={{ marginBottom: isInterview ? 14 : 6 }}>
        <label style={labelStyle}>Feedback</label>
        <textarea rows={isTest ? 8 : 4} style={{ ...inputStyle, resize: "vertical" }} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Your write-up on the candidate's performance…" />
      </div>

      {isInterview && (
        <div style={{ marginBottom: 6 }}>
          <label style={labelStyle}>Scorecard</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {INTERVIEW_PARAMETERS.map((p) => (
              <div key={p.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, color: T.ink2 }}>{p.label}</span>
                <StarRating value={ratings[p.key]} onChange={setRating(p.key)} />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{`
        @media (max-width: 700px) {
          .plc-myresult-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div style={{ ...modalStyle, maxWidth: isTest ? 740 : 620, width: "94%", maxHeight: "88vh", padding: 0, display: "flex", flexDirection: "column" }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "20px 24px", borderBottom: `1px solid ${T.border}`, flexShrink: 0,
        }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>
            {entry.pending ? `Add ${stageLabel} Result` : `Edit ${stageLabel} Result`}
          </span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        <div className="plc-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 24px" }}>
          <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 18 }}>
            {entry.positionApplied || "Role not specified"}{entry.roundLabel ? ` · ${entry.roundLabel}` : ""}
          </div>

          <div className="plc-myresult-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            {isTest ? (
              <>
                <div style={{ minWidth: 0 }}>{sectionMarksTable}</div>
                <div style={{ minWidth: 0 }}>{scoreOutcomeBlock}{feedbackBlock}</div>
              </>
            ) : (
              <>
                <div style={{ minWidth: 0 }}>{scoreOutcomeBlock}</div>
                <div style={{ minWidth: 0 }}>{feedbackBlock}</div>
              </>
            )}
          </div>
        </div>

        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 12,
          padding: "14px 24px", borderTop: `1px solid ${T.border}`, flexShrink: 0,
        }}>
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            style={{ ...primaryBtnStyle, opacity: saving ? 0.55 : 1, cursor: saving ? "not-allowed" : "pointer" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Navbar-agnostic content — Admin/Manager/Employee wrapper pages each embed
// this with their own navbar (same pattern as PlacementAdminPage), since
// anyone can be assigned as a tester/interviewer regardless of role.
const TABS = [
  { value: "TEST", label: "Test" },
  { value: "INTERVIEW", label: "Interview" },
];

// Assignments are fetched in one shot (no server pagination for a single
// person's list — see api.getMyAssignments), so pagination here just
// slices the already-fetched, tab-filtered array client-side.
const ROWS_OPTIONS = [
  { value: "10", label: "10" },
  { value: "25", label: "25" },
  { value: "50", label: "50" },
];

export default function MyInterviewsPage() {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("TEST");
  const [editTarget, setEditTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [resumeTarget, setResumeTarget] = useState(null); // { candidateId, fileName }
  const [testResultTarget, setTestResultTarget] = useState(null); // { testResult, candidateName }
  const [page, setPage] = useState(0); // 0-based, paginated client-side over the already-fetched list
  const [pageSize, setPageSize] = useState(10);

  const load = () => {
    const employeeId = localStorage.getItem("employeeId");
    if (!employeeId) { setLoading(false); return; }
    setLoading(true);
    api.getMyAssignments(employeeId)
      .then((list) => setAssignments(list || []))
      .catch(() => toast.error("Failed to load your assignments"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Switching tabs or the rows-per-page count invalidates whatever page you
  // were on — jump back to page 1 rather than risk landing past the end of
  // a now-smaller filtered list.
  useEffect(() => { setPage(0); }, [activeTab, pageSize]);

  // Resume is only shown on the Interview tab — a Test round has no resume
  // decision to make at that point, the resume already mattered upstream
  // (Screening); by Interview it's the more relevant reference.
  const showResumeColumn = activeTab === "INTERVIEW";
  // So the interviewer can check how the candidate did at Test before
  // conducting the interview — the Test tab already IS that candidate's
  // test result, no separate column needed there.
  const showTestResultColumn = activeTab === "INTERVIEW";
  const filtered = assignments.filter((a) => a.stage === activeTab);
  const testCount = assignments.filter((a) => a.stage === "TEST").length;
  const interviewCount = assignments.filter((a) => a.stage === "INTERVIEW").length;
  const totalPages = filtered.length === 0 ? 0 : Math.ceil(filtered.length / pageSize);
  const pagedFiltered = filtered.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: T.bg }}>
      <GlobalStyles />
      <div style={{ flex: 1, overflowY: "auto", padding: "28px 32px" }} className="plc-scroll">
        <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 22, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: T.primary, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FaClipboardList size={15} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 21, color: T.ink1, fontWeight: 700, fontFamily: T.fontDisplay }}>Recruitment Panel</h2>
              <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>Tests / interviews you've been assigned to conduct</div>
            </div>
          </div>
          <InfoTicker
            text={`Viewing, resume access, and editing/filling result details are enabled only within ${RESULT_WINDOW_HOURS} hours of each scheduled time.`}
            style={{ flex: 1, minWidth: 220 }}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, marginBottom: 18, borderBottom: `1px solid ${T.border}`, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 8 }}>
            {TABS.map((t) => {
              const count = t.value === "TEST" ? testCount : interviewCount;
              const active = activeTab === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setActiveTab(t.value)}
                  style={{
                    border: "none", background: "transparent", cursor: "pointer",
                    padding: "10px 4px", marginBottom: -1, fontFamily: T.fontBody,
                    fontSize: 13.5, fontWeight: 700, color: active ? T.primary : T.ink3,
                    borderBottom: active ? `2px solid ${T.primary}` : "2px solid transparent",
                    display: "flex", alignItems: "center", gap: 7,
                  }}
                >
                  {t.label}
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 20,
                    background: active ? T.primarySoft : "#f1f5f9", color: active ? T.primary : T.ink3,
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: T.ink3, fontWeight: 600, whiteSpace: "nowrap" }}>Rows</span>
            <div style={{ width: 76 }}>
              <PillSelect
                value={String(pageSize)}
                options={ROWS_OPTIONS}
                onChange={(v) => setPageSize(Number(v))}
                placeholder="Rows"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <Spinner padding="80px 0" />
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: T.ink3 }}>
            <FaClipboardList size={28} style={{ marginBottom: 10, opacity: 0.5 }} />
            <div style={{ fontSize: 14, fontWeight: 600 }}>No {activeTab === "TEST" ? "tests" : "interviews"} yet</div>
            <div style={{ fontSize: 12.5, marginTop: 4 }}>You'll see it here when you're assigned to conduct one.</div>
          </div>
        ) : (
          <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.border}`, overflow: "hidden" }}>
            <div className="plc-scroll" style={{ overflow: "auto", maxHeight: "calc(100vh - 350px)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...theadCellStyle, width: 56 }}>Sr.No</th>
                  <th style={theadCellStyle}>Name</th>
                  <th style={theadCellStyle}>Position Applied</th>
                  <th style={theadCellStyle}>Stage</th>
                  <th style={theadCellStyle}>Result</th>
                  {showTestResultColumn && <th style={theadCellStyle}>Test Result</th>}
                  {showResumeColumn && <th style={theadCellStyle}>Resume</th>}
                  <th style={theadCellStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {pagedFiltered.map((a, idx) => {
                  const windowStatus = getResultWindowStatus(a.scheduledDate);
                  const disabledBtnStyle = { ...iconBtnStyle, color: T.ink3, cursor: "not-allowed", opacity: 0.4 };
                  return (
                    <tr key={a.historyId} style={{ borderTop: `1px solid ${T.border}` }}>
                      <td style={{ ...tdCellStyle, color: T.ink3, fontFamily: T.fontMono, fontSize: 12 }}>{page * pageSize + idx + 1}</td>
                      <td style={{ ...tdCellStyle, fontWeight: 600, color: T.ink1 }}>{a.candidateName}</td>
                      <td style={tdCellStyle}>{a.positionApplied || "—"}</td>
                      <td style={tdCellStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                          <StagePill stage={a.stage} />
                          {a.roundLabel && <span style={{ fontSize: 12, color: T.ink2 }}>{a.roundLabel}</span>}
                        </div>
                        <div style={{ fontSize: 11.5, color: T.ink3, fontFamily: T.fontMono, marginTop: 4 }}>{formatDateTime(a.scheduledDate)}</div>
                      </td>
                      <td style={tdCellStyle}><OutcomePill outcome={a.outcome} /></td>
                      {showTestResultColumn && (
                        <td style={tdCellStyle}>
                          {a.testResult ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <OutcomePill outcome={a.testResult.outcome} />
                              <button
                                type="button"
                                disabled={!windowStatus.enabled}
                                onClick={() => windowStatus.enabled && setTestResultTarget({ testResult: a.testResult, candidateName: a.candidateName })}
                                title={windowStatus.enabled ? "View test result" : windowStatus.reason}
                                style={windowStatus.enabled ? iconBtnStyle : disabledBtnStyle}
                              >
                                <FaEye size={12} />
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: T.ink3, fontSize: 12.5 }}>Not taken</span>
                          )}
                        </td>
                      )}
                      {showResumeColumn && (
                        <td style={tdCellStyle}>
                          {a.hasResume ? (
                            <button
                              type="button"
                              disabled={!windowStatus.enabled}
                              onClick={() => windowStatus.enabled && setResumeTarget({ candidateId: a.candidateId, fileName: a.resumeOriginalFileName })}
                              title={windowStatus.enabled ? "View resume" : windowStatus.reason}
                              style={windowStatus.enabled ? iconBtnStyle : disabledBtnStyle}
                            >
                              <FaFilePdf size={13} color={windowStatus.enabled ? T.danger : T.ink3} />
                            </button>
                          ) : (
                            <span style={{ color: T.ink3 }}>—</span>
                          )}
                        </td>
                      )}
                      <td style={tdCellStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <button
                            type="button"
                            disabled={!windowStatus.enabled}
                            onClick={() => windowStatus.enabled && setViewTarget(a)}
                            title={windowStatus.enabled ? "View" : windowStatus.reason}
                            style={windowStatus.enabled ? iconBtnStyle : disabledBtnStyle}
                          >
                            <FaEye size={13} />
                          </button>
                          <button
                            type="button"
                            disabled={!windowStatus.enabled}
                            onClick={() => windowStatus.enabled && setEditTarget(a)}
                            title={windowStatus.enabled ? (a.pending ? "Add Result" : "Edit Result") : windowStatus.reason}
                            style={windowStatus.enabled ? iconBtnStyle : disabledBtnStyle}
                          >
                            <FaPen size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderTop: `1px solid ${T.border}`, flexWrap: "wrap", gap: 10 }}>
              <div style={{ fontSize: 12.5, color: T.ink3 }}>
                Showing {pagedFiltered.length ? page * pageSize + 1 : 0}–{page * pageSize + pagedFiltered.length} of {filtered.length}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  style={{ ...textBtnStyle, padding: "6px 10px", opacity: page === 0 ? 0.4 : 1, cursor: page === 0 ? "not-allowed" : "pointer" }}
                >
                  Previous
                </button>
                <span style={{ fontSize: 12.5, color: T.ink2, fontWeight: 600 }}>
                  Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                  style={{ ...textBtnStyle, padding: "6px 10px", opacity: page >= totalPages - 1 ? 0.4 : 1, cursor: page >= totalPages - 1 ? "not-allowed" : "pointer" }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {viewTarget && (
        <ViewResultModal entry={viewTarget} onClose={() => setViewTarget(null)} />
      )}
      {editTarget && (
        <AddResultModal entry={editTarget} onClose={() => setEditTarget(null)} onDone={load} />
      )}
      {testResultTarget && (
        <TestResultPreviewModal
          testResult={testResultTarget.testResult}
          candidateName={testResultTarget.candidateName}
          onClose={() => setTestResultTarget(null)}
        />
      )}
      {resumeTarget && (
        <ResumeViewerModal candidateId={resumeTarget.candidateId} fileName={resumeTarget.fileName} onClose={() => setResumeTarget(null)} />
      )}
    </div>
  );
}
